import axios from 'axios';
import {
  GET_COMPANIES,
  GET_USER_ROLES,
  ROLE_GROUPS,
  get_request_config
} from '@/utils/system_constants/constant.js';
import {
  first_present,
  normalized_role_projects,
  project_items,
  role_form_values,
  role_group_name,
  role_id,
  role_name
} from './manage_role_helpers';

function role_input_error(component, { role_name, new_group_name, hourly_rate }) {
  if (!role_name) {
    return 'Role name cannot be empty.';
  }

  if (component.new_role_group_is_duplicate(new_group_name)) {
    return 'This role group already exists. Select it from the Role Group list instead.';
  }

  if (hourly_rate === false) {
    return 'Hourly rate must be a number greater than or equal to zero.';
  }

  return '';
}

export default {
  parse_api_data(payload) {
    if (typeof payload === 'string') {
      let parsed_payload = {};

      try {
        parsed_payload = JSON.parse(payload);
      } catch {
        parsed_payload = {};
      }

      return parsed_payload;
    }

    return payload || {};
  },
  map_role(role = {}) {
    const normalized_role = {};
    normalized_role.id = role_id(role);
    normalized_role.name = role_name(role);
    normalized_role.description = String(first_present([role.description, role.role_description])).trim();
    normalized_role.group_id = first_present([role.group_id, role.group]) || null;
    normalized_role.group_name = String(role_group_name(role)).trim();
    normalized_role.hourly_rate = role.hourly_rate ?? null;
    normalized_role.metadata = role.metadata || {};
    normalized_role.is_active = role.is_active !== false;
    normalized_role.assigned_project_ids = normalized_role_projects(role).map(String);
    return normalized_role;
  },
  parse_error_message(error, fallback) {
    const response_data = this.parse_api_data(error?.response?.data);
    return (
      response_data.message ||
      response_data.data ||
      error.message ||
      fallback
    );
  },
  reset_create_form() {
    this.create_form = {
      role_name: '',
      role_description: '',
      group_id: '',
      new_group_name: '',
      hourly_rate: ''
    };
  },
  reset_create_group_form() {
    this.create_group_form = {
      name: '',
      description: ''
    };
  },
  reset_edit_form() {
    this.edit_form = {
      role_id: null,
      role_name: '',
      role_description: '',
      group_id: '',
      new_group_name: '',
      hourly_rate: ''
    };
  },
  get_role_group_name(role) {
    return role?.group_name || this.role_groups.find((group) => String(group.id) === String(role?.group_id))?.name || '';
  },
  normalize_text(value) {
    return String(value || '').trim();
  },
  normalize_lower_text(value) {
    return this.normalize_text(value).toLowerCase();
  },
  find_role_group_by_name(name) {
    const normalized_name = this.normalize_lower_text(name);
    return this.role_groups.find((group) => this.normalize_lower_text(group.name) === normalized_name);
  },
  role_group_name_exists(name) {
    return Boolean(this.find_role_group_by_name(name));
  },
  is_blank_hourly_rate(value) {
    return [null, undefined, ''].includes(value);
  },
  is_valid_hourly_rate(value) {
    return !Number.isNaN(value) && value >= 0;
  },
  normalize_hourly_rate(value) {
    if (this.is_blank_hourly_rate(value)) {
      return null;
    }

    const numeric_value = Number(value);
    if (!this.is_valid_hourly_rate(numeric_value)) {
      return false;
    }

    return numeric_value.toFixed(2);
  },
  get_role_form_values(form) {
    return role_form_values(this, form);
  },
  new_role_group_is_duplicate(new_group_name) {
    return Boolean(new_group_name) && this.role_group_name_exists(new_group_name);
  },
  validate_role_payload_inputs(form_values) {
    const error_message = role_input_error(this, form_values);
    if (error_message) {
      this.show_error(error_message);
      return false;
    }

    return true;
  },
  is_same_role_name(role, role_name) {
    return this.normalize_lower_text(role.name) === role_name.toLowerCase();
  },
  is_same_role_group(role, group_id, new_group_name) {
    if (group_id) {
      return String(role.group_id || '') === group_id;
    }

    if (new_group_name) {
      return this.normalize_lower_text(this.get_role_group_name(role)) === new_group_name.toLowerCase();
    }

    return false;
  },
  is_different_role(role, form_role_id, include_role_id) {
    return !include_role_id || String(role.id) !== String(form_role_id);
  },
  is_duplicate_role_candidate(role, form, form_values, include_role_id) {
    if (!this.is_same_role_name(role, form_values.role_name)) {
      return false;
    }

    if (!this.is_different_role(role, form.role_id, include_role_id)) {
      return false;
    }

    return this.is_same_role_group(role, form_values.group_id, form_values.new_group_name);
  },
  has_duplicate_role(form, form_values, include_role_id) {
    if (!form_values.group_id && !form_values.new_group_name) {
      return false;
    }

    return Boolean(this.roles.find((role) => this.is_duplicate_role_candidate(role, form, form_values, include_role_id)));
  },
  create_role_payload(form_values) {
    const payload = {};
    payload.role_name = form_values.role_name;
    payload.role_description = form_values.role_description;
    payload.hourly_rate = form_values.hourly_rate === null ? null : form_values.hourly_rate;
    return payload;
  },
  add_role_id_to_payload(payload, form, include_role_id) {
    if (include_role_id) {
      payload.role_id = Number(form.role_id);
    }
  },
  add_role_group_to_payload(payload, { group_id, new_group_name }) {
    if (group_id) {
      payload.group_id = Number(group_id);
      return;
    }

    if (new_group_name) {
      payload.group_name = new_group_name;
    }
  },
  build_role_payload(form, include_role_id = false) {
    const form_values = this.get_role_form_values(form);

    if (!this.validate_role_payload_inputs(form_values)) {
      return null;
    }

    if (this.has_duplicate_role(form, form_values, include_role_id)) {
      this.show_error('A role with this name and group already exists.');
      return null;
    }

    const payload = this.create_role_payload(form_values);
    this.add_role_id_to_payload(payload, form, include_role_id);
    this.add_role_group_to_payload(payload, form_values);
    return payload;
  },
  async fetch_roles() {
    this.is_loading = true;

    try {
      const response = await axios.get(GET_USER_ROLES, get_request_config());
      const response_data = this.parse_api_data(response.data);
      const items = Array.isArray(response_data.data) ? response_data.data : [];

      this.roles = items
        .map((role) => this.map_role(role))
        .filter((role) => role.id != null || role.name);
    } catch {
      this.roles = [];
      this.show_error('Failed to load roles.');
    } finally {
      this.is_loading = false;
    }
  },
  async fetch_role_groups() {
    this.role_groups_loading = true;

    try {
      const response = await axios.get(ROLE_GROUPS, get_request_config());
      const response_data = this.parse_api_data(response.data);
      const items = Array.isArray(response_data.data) ? response_data.data : [];

      this.role_groups = items.map((group) => ({
        id: group.id,
        name: String(group.name || '').trim(),
        description: String(group.description || '').trim(),
        is_active: group.is_active !== false
      })).filter((group) => group.id && group.name);
    } catch {
      this.role_groups = [];
      this.show_error('Failed to load role groups.');
    } finally {
      this.role_groups_loading = false;
    }
  },
  async fetch_projects() {
    this.projects_loading = true;

    try {
      const response = await axios.get(GET_COMPANIES, get_request_config());
      const response_data = this.parse_api_data(response.data);
      this.projects = project_items(response_data);
    } catch {
      this.projects = [];
    } finally {
      this.projects_loading = false;
    }
  }
};
